/**
 * Template Picker View
 *
 * Shows after use case selection to offer different ways to start.
 * Single-screen layout with all options visible for easy comparison.
 */

import React from 'react';
import { FileText, Users, User } from 'lucide-react';
import { UseCaseType } from '../../domains/genogram/models/UseCase';
import { useTranslation } from '@/hooks/useTranslation';
import { Tracker } from '../../domains/analytics/services/Tracker';
import { SessionMetrics } from '../../domains/analytics/services/SessionMetrics';
import { getUseCaseById } from '../../domains/genogram/config/useCases';
import { cn } from '../../lib/utils';
import {
  StartPickerShell,
  StartPickerBackLink,
  StartPickerGenerateButton,
  StartPickerCard,
  StartPickerBlankCard,
} from '../start-picker/StartPicker';

interface TemplatePickerViewProps {
  useCase: UseCaseType;
  onSelectTemplate: (templateId: string, config?: TemplateConfig) => void | Promise<void>;
  onGenerateFromText: () => void;
  onStartBlank: () => void;
  onBackToUseCase: () => void;
  backButtonLabel?: string;
  showBlankOption?: boolean;
  isInCanvasDialog?: boolean;
}

export interface TemplateConfig {
  generations: number;
  includeSiblings: boolean;
  includeExtended: boolean;
}

interface QuickTemplate {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  onClick?: () => void;
}

export const TemplatePickerView: React.FC<TemplatePickerViewProps> = ({
  useCase,
  onSelectTemplate,
  onGenerateFromText,
  onStartBlank,
  onBackToUseCase,
  backButtonLabel,
  showBlankOption = true,
  isInCanvasDialog = false,
}) => {
  const { t } = useTranslation('genogram');
  const useCaseData = getUseCaseById(useCase);
  const isFamilyTherapy = useCase === 'family-therapy';
  const isMedicalGenetic = useCase === 'medical-genetic';
  const isGeneralResearch = useCase === 'general-research';

  const handleStartBlank = () => {
    Tracker.track('onboarding.template.selected', { template: 'blank', useCase });
    SessionMetrics.firstTime('onboarding.completed');
    onStartBlank();
  };

  // Quick templates - simplified to key options
  const quickTemplates: QuickTemplate[] = (() => {
    if (isFamilyTherapy) {
      return [
        {
          id: "conflict-map",
          name: t("templatePicker.familyTherapy.conflictMap", { defaultValue: "The Conflict Map" }),
          description: t("templatePicker.familyTherapy.conflictMapDesc", { defaultValue: "High-tension relationships & co-parenting" }),
          icon: <Users className="size-5" />,
        },
        {
          id: "legacy-map",
          name: t("templatePicker.familyTherapy.legacyMap", { defaultValue: "The Legacy Map" }),
          description: t("templatePicker.familyTherapy.legacyMapDesc", { defaultValue: "Tracking 3 generations of health & habits" }),
          icon: <Users className="size-5" />,
        },
        {
          id: "fusion-map",
          name: t("templatePicker.familyTherapy.fusionMap", { defaultValue: "The Fusion Map" }),
          description: t("templatePicker.familyTherapy.fusionMapDesc", { defaultValue: "Blended families & boundary setting" }),
          icon: <User className="size-5" />,
        }
      ];
    }

    if (isMedicalGenetic) {
      return [
        {
          id: "medical-history-map",
          name: t("templatePicker.medicalGenetic.medicalHistory", { defaultValue: "Medical History" }),
          description: t("templatePicker.medicalGenetic.medicalHistoryDesc", { defaultValue: "Maps disease trends to assess clinical risk." }),
          icon: <Users className="size-5" />,
        },
        {
          id: "genetic-disoder-map",
          name: t("templatePicker.medicalGenetic.geneticPedigree", { defaultValue: "Genetic Pedigree" }),
          description: t("templatePicker.medicalGenetic.geneticPedigreeDesc", { defaultValue: "Maps biological inheritance to track hereditary disorders." }),
          icon: <Users className="size-5" />,
        },
        {
          id: "phychosocial-map",
          name: t("templatePicker.medicalGenetic.psychosocial", { defaultValue: "Psychosocial" }),
          description: t("templatePicker.medicalGenetic.psychosocialDesc", { defaultValue: "Miller-Chen family: anxiety, enmeshment, achievement pressure." }),
          icon: <User className="size-5" />,
        }
      ];
    }

    if (isGeneralResearch) {
      return [
        {
          id: "genetic-history-map",
          name: t("templatePicker.generalResearch.geneticHistory", { defaultValue: "Genetic History Map" }),
          description: t("templatePicker.generalResearch.geneticHistoryDesc", { defaultValue: "Maps hereditary health patterns and biological traits." }),
          icon: <Users className="size-5" />,
        },
        {
          id: "behavioral-pattern-map",
          name: t("templatePicker.generalResearch.behavioralPattern", { defaultValue: "Behavioral Pattern Map" }),
          description: t("templatePicker.generalResearch.behavioralPatternDesc", { defaultValue: "Traces recurring social habits and family dynamics." }),
          icon: <Users className="size-5" />,
        },
        {
          id: "heritage-roots-map",
          name: t("templatePicker.generalResearch.heritageRoots", { defaultValue: "Heritage & Roots Map" }),
          description: t("templatePicker.generalResearch.heritageRootsDesc", { defaultValue: "Documents migration history, social status, and lineage." }),
          icon: <User className="size-5" />,
        }
      ];
    }

    return [
      {
        id: "2-gen",
        name: t("templatePicker.twoGenerations", { defaultValue: "2 Generations" }),
        description: t("templatePicker.twoGenerationsDesc", { defaultValue: "You + parents" }),
        icon: <Users className="size-5" />,
      },
      {
        id: "3-gen",
        name: t("templatePicker.threeGenerations", { defaultValue: "3 Generations" }),
        description: t("templatePicker.threeGenerationsDesc", { defaultValue: "Include grandparents" }),
        icon: <Users className="size-5" />,
      },
      {
        id: "simple",
        name: t("templatePicker.singlePerson", { defaultValue: "Single Person" }),
        description: t("templatePicker.singlePersonDesc", { defaultValue: "Just me to start" }),
        icon: <User className="size-5" />,
      }
    ];
  })();

  const handleSelectTemplate = (templateId: string, config?: TemplateConfig) => {

    Tracker.track('onboarding.template.selected', { template: templateId, useCase, ...config });
    SessionMetrics.firstTime('onboarding.completed');
    onSelectTemplate(templateId, config);
  };

  const handleGenerateFromText = () => {
    Tracker.track('onboarding.template.selected', { template: 'generate', useCase });
    SessionMetrics.firstTime('onboarding.completed');
    onGenerateFromText();
  };

  return (
    <StartPickerShell
      icon={useCaseData?.icon ?? <FileText className="size-5" />}
      title={isInCanvasDialog
        ? t('templatePicker.canvasTitle', { defaultValue: 'Choose a starting point' })
        : useCaseData?.name ?? t('templatePicker.title', { defaultValue: 'How would you like to start?' })}
      subtitle={isInCanvasDialog
        ? t('templatePicker.canvasSubtitle', {
          defaultValue: 'Use a ready-made family structure, or build one with AI.',
        })
        : t('templatePicker.categorySubtitle', { defaultValue: 'Choose a scenario template that best fits your case' })}
      testId="tid.onboarding.templatePicker"
      compact={isInCanvasDialog}
      maxWidthClass={cn(isInCanvasDialog ? 'max-w-none' : 'max-w-2xl')}
      topSlot={
        <StartPickerBackLink
          label={backButtonLabel ?? t('templatePicker.backToCategories', { defaultValue: 'Back to categories' })}
          onClick={onBackToUseCase}
          testId="tid.onboarding.templatePicker.back"
        />
      }
    >
      <StartPickerGenerateButton
        title={t('templatePicker.buildWithAI', { defaultValue: 'Build with AI' })}
        description={t('templatePicker.generateFromTextDesc', {
          defaultValue: "Describe your family in natural language and we'll draft an editable genogram.",
        })}
        onClick={handleGenerateFromText}
        testId="tid.onboarding.templatePicker.generate"
        dataEvent={{ click: 'onboarding.step2', params: { value: 'generate' } }}
      />
      {isInCanvasDialog && (
        <p className="mb-3 text-xs text-muted-foreground">
          {t('templatePicker.readyMadeDescription', {
            defaultValue: 'Ready-made templates are added to this canvas and remain fully editable.',
          })}
        </p>
      )}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {/* Clean single glyphs (template.icon), not the legacy mini-diagram
            previews — those read as noise inside the 40px well. */}
        {quickTemplates.map((template) => (
          <StartPickerCard
            key={template.id}
            icon={template.icon}
            name={template.name}
            description={template.description}
            onClick={() => {
              if (template.onClick) {
                template.onClick();
                return;
              }
              handleSelectTemplate(template.id);
            }}
            testId={`tid.onboarding.templatePicker.template.${template.id}`}
            dataEvent={{ click: 'onboarding.step2', params: { value: template.id } }}
          />
        ))}
        {showBlankOption && (
          <StartPickerBlankCard
            name={t('templatePicker.blankShort', { defaultValue: 'Blank' })}
            description={t('templatePicker.blankShortDesc', {
              defaultValue: 'Start from an empty canvas.',
            })}
            onClick={handleStartBlank}
            testId="tid.onboarding.templatePicker.template.blank"
            dataEvent={{ click: 'onboarding.step2', params: { value: 'blank' } }}
          />
        )}
      </div>
    </StartPickerShell>
  );
};
